import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from "react-router-dom";
import { reverse } from 'named-urls';
import { Dropdown } from 'react-bootstrap';
import { useParams } from 'react-router';

import { appActions } from '@/store/actions/app.actions';
import viewerActions from '@/store/actions/eventViewer.actions';

import api from "@/services/api";

import { useShowModal } from '@Elements/Modal/ModalHooks';
import Button from "@Elements/Button/Button";

import url from "@/router/urls";

import updateUrl from "@/utils/eventViewerHelper";

import LinkButton from "@Elements/Button/LinkButton";
import Spinner from "@Elements/Spinner/Spinner";
import AuthHeader from "@Templates/header/AuthHeader/AuthHeader";

import SearchInput from "@Templates/footer/ViewerFooter/SearchInput";
import HowToStartMenu from "@Templates/header/ViewerHeader/parts/HowToStartMenu";

import wlt_logo from '@Assets/images/wlt_logo_small.svg';

const ViewerHeader = () => {
	const viewer = useSelector((state) => state.viewer);
	const viewerData = useSelector((state) => state.viewer.data);
	const { t } = useTranslation();

	const isMobile = useSelector((state) => state.viewer.isMobile);
	const swapPanels = useSelector((state) => state.app.viewer.swapPanels);
	const transcriptionPagesData = useSelector((state) => state.viewer.transcriptionPagesData);
	const searchResults = useSelector((state) => state.viewer.searchResults);
	const selectedLayer = useSelector(state => state.viewer.selectedLayer);
	const dispatch = useDispatch();
	const history = useHistory();
	const { eventId, pageId } = useParams();

	const viewMode = viewerData.viewMode;
	const readOnly = viewMode === 'read-only' || viewMode === 'student';
	const noAccess = useSelector( (state) => state.viewer.noAccess );

	const [ isMenuShown, setMenuShown ] = useState(false);

	const [ pageNumber, setPageNumber ] = useState("");
	const [ currentPageNumber, setCurrentPageNumber ] = useState("");

	const [ searchedPhrase, setSearchedPhrase ] = useState(searchResults.searchedPhrase);

	useEffect(() => {
		setSearchedPhrase(searchResults.searchedPhrase);
	}, [searchResults.searchedPhrase]);

	const changeSwapPanels = () => {
		dispatch(appActions.setSwapPanels(!swapPanels));
	};

	const downloadFileFormat = (format) => {
		dispatch(viewerActions.getFileByFormat(viewerData.id, format));
	};

	const modalExportTextLayers = useShowModal({
		title:t('modals.viewerExportTextLayers.title'),
		type: 'viewer',
		name: 'viewerExportTextLayers',
		componentPath: './parts/ModalExportTextLayers',
		componentProps: {
			downloadFileFunction: downloadFileFormat,
		},
		withClose: true,
	});

	const modalMarkTranscriptionPageAsEmpty = useShowModal({
		title: t('modals.markTranscriptionPageAsEmpty.title'),
		name: "markTranscriptionPageAsEmpty",
		type: 'viewer',
		componentPath: "./parts/ModalMarkTranscriptionPageAsEmpty",
		componentProps: {
			pageId: viewerData.id,
			handleOnComplete: () => {
				dispatch(viewerActions.setPageEmpty(true));
				dispatch(viewerActions.allowUndoDisallowRedo());
				dispatch(viewerActions.loadData(parseInt(eventId), parseInt(pageId)));
			},
		},
	});

	const setPageAsNotEmpty = () => {
		api.put(`/pages/${pageId}/empty?empty=false`).then(() => {
			dispatch(viewerActions.setPageEmpty(false));
		});
	};

	useEffect(() => {
		if(viewerData.pageNo != currentPageNumber){
			setCurrentPageNumber(viewerData.pageNo ? viewerData.pageNo : "");
			setPageNumber(viewerData.pageNo ? viewerData.pageNo : "");
		}
	}, [viewerData.pageNo]);

	return !noAccess ? (
		<>
			<Spinner
				stateFromStore
				viewer
			/>
			<header className='viewer-header'>
				<div className={`viewer-header__main ${isMobile && isMenuShown ? 'viewer-header__main--mobile-close' : '' }`}>
					{!isMobile && 
					<div
						className='viewer-header__logo'
						data-tooltip-id='custom-tooltip' data-tooltip-content={t('viewer.tooltip.header.homeBt') }
						data-place='right'
					>
						<Link
							to={ url.home }
							className='viewer-header__logo--href'
						>
							<img
								src={ wlt_logo }
								alt={t('headers.logoAlt') }
								className='viewer-header__logo--image'
							/>
						</Link>
					</div>}
					{isMobile &&
					<div className='viewer-header__nav--left'>
						<Dropdown
							className='viewer-header__dropdown'
							onToggle={(isShown) => { setMenuShown(isShown); }}
							show={isMenuShown}
						>
							<div
								className='viewer-btn__wrapper'
								data-place='bottom'
							>
								<Dropdown.Toggle
									className='viewer-header__menu-button'
								>
									{!isMenuShown && <i className='icon-hamburger' />}
									{isMenuShown && <i className='icon-close' />}
								</Dropdown.Toggle>
							</div>

							<Dropdown.Menu className='viewer-header__mobile-dropdown-menu'>
								{!readOnly && <Dropdown.Item onClick={ () => modalMarkTranscriptionPageAsEmpty() }>{t('viewer.header.markAsEmpty')}</Dropdown.Item> }
								<Dropdown.Item onClick={ () => modalExportTextLayers() }>{t('viewer.header.download')}</Dropdown.Item>
								<div className='viewer-header__mobile-dropdown-menu__search-input'>
									<SearchInput searchedPhrase={searchedPhrase} setSearchedPhrase={setSearchedPhrase} onSearch={() => { setMenuShown(false); }}/>
								</div>
							</Dropdown.Menu>
						</Dropdown>
					</div>}
					<div className='viewer-header__title'>
						<p className='viewer-header__text--title'>
							{ 
								isMobile && isMenuShown 
									? 
									t("viewer.header.closeMenu")
									:
									viewerData.titleLabel ? viewerData.titleLabel + " [" + t('viewer.header.transcriptionPage') + " " + currentPageNumber + (viewerData.teiPbValue ? " | " + viewerData.teiPbValue : "") + "]" : ""
							}
						</p>
					</div>
					{!isMobile && 
					<div className='viewer-header__how-to-start'>
						<Dropdown align='end'>
							<Dropdown.Toggle
								id='dropdown-custom-11'
								className='viewer-header__how-to-start-dropdown__button'
								variant='viewer-header__how-to-start-dropdown__button'
							>{t('transcriptionHelpPage.howToTranscribe.title')}
							</Dropdown.Toggle>

							<HowToStartMenu />
						</Dropdown>
					</div>}
					{(!isMobile || isMobile && !isMenuShown) && 
					<div className='viewer-header__info'>
						<LinkButton
							variant='icon'
							className='viewer-header__close'
							onClick={()=>{ dispatch(viewerActions.setSearchResults("", { results: { resources: [], hits: [] } })); }}
							to={ `${reverse(url.event, { id: eventId })}` }
						>
							<span className='d-none'>X</span>
							<i className='icon-close' />
						</LinkButton>
					</div>}
				</div>
				{!isMobile &&
				<nav className='viewer-header__nav'>
					<div className='viewer-header__nav--left'>
						<Dropdown
							className='viewer-header__dropdown'
							onToggle={(isShown) => { setMenuShown(isShown); }}
							show={isMenuShown}
						>
							<div
								className='viewer-btn__wrapper'
								data-place='bottom'
							>
								<Dropdown.Toggle
									className='viewer-header__menu-button'
								>
									{!isMenuShown && <i className='icon-hamburger' />}
									{isMenuShown && <i className='icon-close' />}
									<span>{t('viewer.header.menu')}</span>
								</Dropdown.Toggle>
							</div>

							<Dropdown.Menu>
								<Dropdown.Item onClick={ changeSwapPanels }>{t('viewer.header.swapPanels')}</Dropdown.Item>
								{!readOnly && <Dropdown.Item onClick={ () => viewerData.empty ? setPageAsNotEmpty() : modalMarkTranscriptionPageAsEmpty() }>{t('viewer.header.markAsEmpty')} {viewerData.empty && <i className='icon-accept' />}</Dropdown.Item> }
								<Dropdown.Item onClick={ () => modalExportTextLayers() }>{t('viewer.header.download')}</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
					</div>
					{!readOnly &&
					<div className='viewer-header__nav--left' style={ { marginRight: 'auto', marginLeft: '20px' } }>
						<div
							data-tooltip-id='custom-tooltip' data-tooltip-content={t('viewer.header.undo')}
						>
							<Button
								className='viewer__button--menu viewer-header__nav--redo-undo-button'
								variant='viewer-button--active'
								onClick={()=>{ dispatch(viewerActions.undo(eventId, pageId, selectedLayer)); }}
								disabled={!viewer.editor.undoPossible}
							>
								<i className='icon-undo viewer__menu--icon' />
							</Button>
						</div>
						<div
							data-tooltip-id='custom-tooltip' data-tooltip-content={t('viewer.header.redo')}
						>
							<Button
								className='viewer__button--menu viewer-header__nav--redo-undo-button'
								variant='viewer-button--active'
								onClick={()=>{ dispatch(viewerActions.redo(eventId, pageId, selectedLayer)); }}
								disabled={!viewer.editor.redoPossible}
							>
								<i className='icon-redo viewer__menu--icon' />
							</Button>
						</div>
					</div>}
					<div className='viewer-header__nav--right'>
						<div className='viewer-header__prev-page'>
							<span
								onClick={() => {
									if(viewerData.prevPageId){
										dispatch(viewerActions.loadData(parseInt(eventId), parseInt(viewerData.prevPageId)));
										updateUrl(history, eventId, viewerData.prevPageId, searchResults.searchedPhrase);
									}
								}}
							>
								<i className='icon-arrow-left-full'/>
								{t('viewer.header.pages.prev') + " " +t('viewer.header.pages.page') }
							</span>
						</div>
						<div className='viewer-header__page-number'>
							<input
								type='number'
								value={pageNumber || ""}
								onChange={e => { setPageNumber(e.target.value); }}
								onKeyDown={e => {
									if(e.key === 'Enter'){
										if(transcriptionPagesData.pageToId.get(+pageNumber)){
											dispatch(viewerActions.loadData(parseInt(eventId), parseInt(transcriptionPagesData.pageToId.get(+pageNumber))));
											updateUrl(history, eventId, transcriptionPagesData.pageToId.get(+pageNumber), searchResults.searchedPhrase);
										}
									}
								}}
							/>
							<span> {t('viewer.header.pages.fromNumber') + " " + transcriptionPagesData.pageNum }</span>
						</div>
						<div className='viewer-header__next-page'>
							<span
								onClick={() => {
									if(viewerData.nextPageId){
										dispatch(viewerActions.loadData(parseInt(eventId), parseInt(viewerData.nextPageId)));
										updateUrl(history, eventId, viewerData.nextPageId, searchResults.searchedPhrase);
									}
								}}
							>
								{t('viewer.header.pages.next') + " " +t('viewer.header.pages.page') }
								<i className='icon-arrow-right-full'/>
							</span>
						</div>
					</div>
				</nav>}
			</header>
		</>
	) : (
		<AuthHeader />
	);
};

export default ViewerHeader;